import type { HTMLAttributes, ReactNode } from 'react';
import styled from 'styled-components';
import { colors, getMediumRGBA } from '../../../../styles/colors';
import { DESKTOP_BREAKPOINT } from '../../../../styles/breakpoints';
import { maxOnboardingContentWidth, space, text } from '../../../../styles/onboardingUiTokens';
import { PhysiofitPrivacyNote } from '../content/PhysiofitPrivacyNote';
import { formFieldBorderOpacity, formFieldBorderWidthPx } from '../forms/formFieldTokens';
import { ForwardButton } from './ForwardButton';
import { useOnboardingLayout } from './OnboardingLayoutContext';

export type OnboardingFooterProps = Omit<HTMLAttributes<HTMLElement>, 'children'> & {
  /**
   * Replaces the default primary forward button. Ignored when layout `hideForward` is true.
   */
  children?: ReactNode;
  /** Hides the Datenschutz line — for a footer instance that isn't the main flow footer (e.g. a drawer's own button wrapper). */
  hidePrivacyNote?: boolean;
  /** One short line under the CTA (Figma "Privacy" slot of the button wrapper), e.g. "Du zahlst: 0,00 €". */
  ctaSubline?: ReactNode;
};

const FooterRoot = styled.footer`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  box-sizing: border-box;
  pointer-events: none;
  background: ${colors.white};
  border-top: ${formFieldBorderWidthPx}px solid ${getMediumRGBA(formFieldBorderOpacity)};

  & > * {
    pointer-events: auto;
  }

  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    position: relative;
    background: transparent;
    border-top: 0;
  }
`;

const Inner = styled.div`
  position: relative;
  z-index: 1;
  max-width: ${maxOnboardingContentWidth};
  margin: 0 auto;
  padding: ${space.md} ${space.md} calc(${space.md} + env(safe-area-inset-bottom, 0px));
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${space.sm};

  & > * {
    width: 100%;
  }

  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    border-top: ${formFieldBorderWidthPx}px solid ${getMediumRGBA(formFieldBorderOpacity)};
  }
`;

/** Looks disabled (dimmed) but stays clickable so the screen can surface validation on press. */
const InactiveForwardButton = styled(ForwardButton)`
  opacity: 0.45;
`;

const CtaSubline = styled.p`
  margin: 0;
  text-align: center;
  font-size: ${text.mini.fontSize};
  line-height: ${text.mini.lineHeight};
  color: ${colors.medium};
`;

/**
 * Fixed viewport footer: full-width primary forward (or custom slot) plus the Datenschutz note.
 * Back navigation and progress live in {@link OnboardingHeader}.
 */
export function OnboardingFooter({
  children,
  hidePrivacyNote = false,
  ctaSubline,
  className,
  ...rest
}: OnboardingFooterProps) {
  const { forwardLabel, forwardDisabled, forwardInactive, hideForward, forwardBusy } = useOnboardingLayout();

  const ForwardCta = forwardInactive && !forwardDisabled ? InactiveForwardButton : ForwardButton;

  return (
    <FooterRoot className={className} {...rest}>
      <Inner>
        {!hideForward
          ? (children ?? (
              <ForwardCta type="submit" disabled={forwardDisabled} aria-busy={forwardBusy || undefined}>
                {forwardLabel}
              </ForwardCta>
            ))
          : null}
        {ctaSubline ? <CtaSubline>{ctaSubline}</CtaSubline> : null}
        {hidePrivacyNote ? null : <PhysiofitPrivacyNote />}
      </Inner>
    </FooterRoot>
  );
}
